import type { FC } from 'react';
import React, { Fragment, memo, useCallback, useContext, useEffect, useState, useRef } from 'react';
import { css } from '@compiled/react';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	CREATE_COMMENT_SHORTCUT,
	UNIVERSAL_OPEN_COMMENT_EDITOR,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { DEFAULT_LIMIT } from '@confluence/page-comments-queries';
import { VIEW_PAGE_COMMENTS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useContentState } from '@confluence/content-state/entry-points/useContentState.ts';
import { LoadingPriority } from '@confluence/loadable';
import { CommentPlaceholder } from '@confluence/comment-simple/entry-points/CommentPlaceholder.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { CommentsSectionLoader } from '@confluence/comments-section';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import {
	CommentsSectionQuery,
	CommentsSectionWithoutReactionsQuery,
} from '@confluence/page-comments-queries/entry-points/CommentsSectionQuery.graphql.ts';
import {
	useIsObjectSidebarShown,
	usePanel,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { PanelName } from '@confluence/object-sidebar-api/entry-points/PanelName.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import type {
	CommentsSectionQuery as CommentsSectionQueryType,
	CommentsSectionWithoutReactionsQuery as CommentsSectionWithoutReactionsQueryType,
	CommentsSectionQueryVariables,
} from '@confluence/page-comments-queries/entry-points/__types__/CommentsSectionQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const pageCommentWrapperStyles = css({
	position: 'relative',
	zIndex: 0,
});

const sendKeyboardShortcutAnalytics = (shortcut: string) => {
	void getAnalyticsWebClient().then((analyticsClient) => {
		analyticsClient.sendUIEvent({
			source: 'viewPage',
			action: 'pressed',
			actionSubject: 'keyboardShortcut',
			actionSubjectId: 'createComment',
			attributes: {
				key: shortcut,
			},
		});
	});
};

/**
 * @deprecated This component is deprecated and will be removed in a future version.
 * It is used to render legacy page comments but is no longer needed as part of modernization.
 */
const LazyLoadablePageComments = ({
	children,
	isContentReady,
	shouldLazyLoad,
	renderPlaceholder = undefined,
	loadPageCommentsCallback = undefined,
	isCommentsReady,
	totalCount,
}: {
	children: React.ReactElement;
	isContentReady: boolean;
	shouldLazyLoad?: boolean;
	renderPlaceholder?: () => React.ReactElement | null;
	loadPageCommentsCallback?: () => void;
	isCommentsReady: boolean;
	totalCount: number | undefined;
}) => {
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const observerRef = useRef<IntersectionObserver | null>(null);

	// load comments immediately if not lazy load, otherwise wait until it is in the viewport
	const [loadPageComments, setLoadPageComments] = useState(!shouldLazyLoad);

	const isObjectSidebarShown = useIsObjectSidebarShown();
	const panel = usePanel();

	const isCommentsPanelShown = isObjectSidebarShown && panel?.id === PanelName.CommentsPanel;

	const { setQueryParams } = useRouteActions();

	const isCommentPreloadedForSSR =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		(Boolean(process.env.REACT_SSR) || window.__SSR_RENDERED__) &&
		fg('confluence_frontend_remove_comments_placeholder');

	useEffect(() => {
		// viewport intersection detection for SPA only
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			// eslint-disable-next-line no-restricted-syntax
			observerRef.current = new IntersectionObserver(([entry]) => {
				if (entry.isIntersecting) {
					// show page comments when the wrapper is in viewport
					setLoadPageComments(true);
					observerRef.current?.disconnect();
				}
			});

			// Remove the observer as soon as the component is unmounted
			return () => {
				observerRef.current?.disconnect();
			};
		}
		// NOOP for SSR
	}, []);

	useEffect(() => {
		// viewport intersection detection for SPA only
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (!process.env.REACT_SSR) {
			if (wrapperRef.current && !loadPageComments && isContentReady) {
				// attach intersection observer after content is ready
				observerRef.current?.observe(wrapperRef.current);
			}
		}
	}, [loadPageComments, isContentReady]);

	useEffect(() => {
		if (loadPageComments) {
			loadPageCommentsCallback?.();
		}
	}, [loadPageComments, loadPageCommentsCallback]);

	// render placeholder only in SSR
	const shouldRenderPageComments =
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		!process.env.REACT_SSR &&
		(loadPageComments || (isCommentsPanelShown && fg('confluence_frontend_content_wrapper')));

	const openCommentEditorShortcutCb = (e: KeyboardEvent) => {
		e.preventDefault();
		// This check is necessary to see which keyboard shortcut was used so it can be sent to analytics.
		// e.key is used to get the key that was pressed according to the current keyboard layout.
		// We compare with CREATE_COMMENT_SHORTCUT because it is a single-letter keyboard shortcut.
		// It's more difficult to compare with UNIVERSAL_OPEN_COMMENT_EDITOR because it includes modifiers.  Modifiers which change what e.key maps to depending on the keyboard layout.
		const shortcut =
			e.key === CREATE_COMMENT_SHORTCUT ? CREATE_COMMENT_SHORTCUT : UNIVERSAL_OPEN_COMMENT_EDITOR;
		if (!isCommentsReady) {
			setQueryParams({
				showCommentArea: 'true',
			});
			//This will scroll to and load the page comments section if its not available (e.g. on long pages)
			wrapperRef.current?.scrollIntoView && wrapperRef.current?.scrollIntoView();
		} else {
			window.dispatchEvent(new Event('openPageCommentEditor'));
		}
		sendKeyboardShortcutAnalytics(shortcut);
	};

	return (
		<div data-testid="page-comment-wrapper" ref={wrapperRef} css={pageCommentWrapperStyles}>
			<GeneralShortcutListener
				deprecatedAccelerator={CREATE_COMMENT_SHORTCUT}
				accelerator={UNIVERSAL_OPEN_COMMENT_EDITOR}
				listener={(e) => openCommentEditorShortcutCb(e)}
			/>
			{/* if the page is SSRd and the preloaded call went through successfully but there are no comments, don't show the placeholder as we already know there is nothing to render */}
			{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr*/}
			{(isCommentPreloadedForSSR && totalCount === 0) || shouldRenderPageComments
				? children
				: renderPlaceholder?.()}
		</div>
	);
};

const ViewPageFooterCommentsComponent: FC<{
	contentId: string;
	loadingPriority: LoadingPriority | null;
	classicComments?: React.ComponentType<any>;
	hasReactions?: boolean;
}> = memo(({ contentId, classicComments, loadingPriority, hasReactions }) => {
	const [state] = useContentState();

	/* Preload CommentsSectionQuery - We preload CommentsSectionQuery as part of next/packages/query-preloaders/src/index.ts,
    but its bestEffort Preloading - meaning if it exceeds the threshold it wont wait for query to finish
    Adding preloading here as well to ensure data is ready once user scrolls*/

	const { data, error } = useQueryInterop<
		CommentsSectionQueryType | CommentsSectionWithoutReactionsQueryType,
		CommentsSectionQueryVariables
	>(hasReactions ? CommentsSectionQuery : CommentsSectionWithoutReactionsQuery, {
		variables: {
			contentId,
			offset: '',
			first: DEFAULT_LIMIT,
		},
		fetchPolicy:
			hasReactions &&
			window.__SSR_RENDERED__ &&
			fg('confluence_frontend_remove_comments_placeholder')
				? 'cache-first'
				: 'cache-and-network',
		skip: !contentId,
	});

	/*  Marking all graphql errors here as handled since we are only preloading query. The CommentsSectionFabric component
    where the query data is used has exhaustive error handling*/

	if (error) {
		markErrorAsHandled(error);
	}

	let totalCount;
	// data will be populated on first render only if SSR preloading is successful. In this scenario,
	// the totalCount will be either 0 or some number. For all other cases (either FG is disabled or FG is enabled but there
	// was an issue with preloading which resulted in no cached data), totalCount will be undefined
	if (data?.comments && fg('confluence_frontend_remove_comments_placeholder')) {
		totalCount = data.comments.totalCount;
	}

	const { getQueryParams } = useContext(RoutesContext);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const { focusedCommentId, replyToComment, showCommentArea, editComment, commentId } =
		getQueryParams();

	/* check for query params - If anyone of these are true just load Page comments */
	const isFocusedComment = Boolean(
		focusedCommentId || replyToComment || showCommentArea || (editComment === 'true' && commentId),
	);

	const [isCommentsReady, setIsCommentsReady] = useState(false);

	const startExperienceTracker = useCallback(() => {
		experienceTracker.start({
			name: VIEW_PAGE_COMMENTS_EXPERIENCE,
			attributes: {
				isConditionalRenderingOn: true,
				isFocusedComment,
				editor: 'FABRIC',
				contentId,
			},
		});
	}, [experienceTracker, isFocusedComment, contentId]);

	/* Display comment Placeholder if isContentReady is true - Waiting for content to render to
    display placholder since at times it will display the placeholder towards middle of screen
    and then get pushed down*/
	const renderCommentPlaceholder = useCallback(() => {
		// if the preloading FG is enabled AND there are no comments, there is no need to return a placeholder
		if (totalCount === 0 && fg('confluence_frontend_remove_comments_placeholder')) {
			return null;
		}
		const showPlaceholder =
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			Boolean(process.env.REACT_SSR) || window.__SSR_RENDERED__ || state.isContentReady;
		return <CommentPlaceholder showPlaceholder={showPlaceholder} />;
	}, [state.isContentReady, totalCount]);

	return (
		<LazyLoadablePageComments
			shouldLazyLoad={!isFocusedComment}
			isContentReady={state.isContentReady}
			renderPlaceholder={renderCommentPlaceholder}
			loadPageCommentsCallback={startExperienceTracker}
			isCommentsReady={isCommentsReady}
			totalCount={totalCount}
		>
			<Fragment>
				{
					// Important! Here, the renderCommentPlaceholder() must match the renderPlaceholder
					// in LazyLoadPageComments to ensure the initial hydration matches the SSR render result
					!fg('confluence_frontend_remove_comments_placeholder') &&
						!isCommentsReady &&
						renderCommentPlaceholder()
				}
				<CommentsSectionLoader
					contentId={contentId}
					classicComments={classicComments}
					handlePageCommentsRender={() => setIsCommentsReady(true)}
					loadingPriority={loadingPriority || LoadingPriority.PAINT}
				/>
			</Fragment>
		</LazyLoadablePageComments>
	);
});

export const ViewPageFooterComments = withErrorBoundary({
	attribution: Attribution.COLLABORATION,
})(ViewPageFooterCommentsComponent);
